import { Button, ConfirmDialog, useSnackbarContext } from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import { Box, Card, Stack, Typography } from "@mui/material";
import { orange } from "@mui/material/colors";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ConnectionStatus, SystemUserStatus } from "../api/graphql";
import { appendItem } from "../client";
import { CreatePendingConnectionDocument } from "../ConnectionsFlow/CreatePendingConnection.generated";
import { CreatePendingProfessionalConnectionDocument } from "../ConnectionsFlow/CreatePendingProfessionalConnection.generated";
import { ContentSkeleton } from "../lib/closet";
import DetailsStack, { DetailsStackItem } from "../lib/DetailsStack";
import { getDocumentTypeLabel } from "../lib/DocumentType";
import getHostname from "../lib/getHostname";
import useApolloErrorHandler from "../lib/handleApolloError";
import hasInAppHistory from "../lib/hasInAppHistory";
import { ExternalLink } from "../lib/Link";
import Quotation from "../lib/Quotation";
import { relationshipToHumanReadable } from "../lib/Relationship";
import Screen from "../lib/Screen";
import { getRawDateTzUnaware, monthDateYearFormat } from "../lib/timeFormats";
import { useCurrentVisitor } from "../SessionBoundary";
import EditConnectionRelationshipDialog from "./EditConnectionRelationshipDialog";
import { GetContactDetailsDocument } from "./GetContactDetails.generated";

function EditableCard({
  children,
  onEdit,
}: {
  children: React.ReactNode;
  // read-only when unset
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <DetailsStack>{children}</DetailsStack>
        </Box>
        {onEdit && (
          <Button variant="text" onClick={onEdit}>
            {t("Edit")}
          </Button>
        )}
      </Box>
    </Card>
  );
}

export default function ReviewAndResubmitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleApolloError = useApolloErrorHandler();
  const snackbarContext = useSnackbarContext();
  const user = useCurrentVisitor();

  const { connectionId } = useParams<{ connectionId: string }>();
  if (!connectionId) throw new Error("missing connectionId param");

  // an edited relationship is kept in the URL so that it survives
  // a round trip through the identification flow
  const [searchParams, setSearchParams] = useSearchParams();

  const [editRelationshipShown, setEditRelationshipShown] = useState(false);
  const [confirmEditIdShown, setConfirmEditIdShown] = useState(false);
  // once resubmitted the connection is no longer rejected; this keeps the
  // status guard below from racing the post-submit navigation
  const [submitted, setSubmitted] = useState(false);

  const { data, error } = useQuery(GetContactDetailsDocument, {
    fetchPolicy: "cache-and-network",
    variables: { connectionId },
  });

  const onResubmitError = (e: unknown) => {
    setSubmitted(false);
    handleApolloError(e);
  };
  const onResubmitted = () => {
    snackbarContext.alert(
      "success",
      t(
        "Your request to connect with {{fullName}} will be reviewed by facility staff shortly.",
        { fullName: data?.connection.inmate.fullName }
      )
    );
    // the same connection is reused, so pop back to its details entry
    // rather than pushing a duplicate of it
    if (hasInAppHistory()) navigate(-1);
    else navigate(`/contacts/${connectionId}`, { replace: true });
  };

  const [createPendingConnection, { loading: resubmittingPersonal }] =
    useMutation(CreatePendingConnectionDocument, {
      onError: onResubmitError,
      update: (cache, { data: createPendingData }) => {
        if (!createPendingData) return;
        cache.modify({
          id: cache.identify({ __typename: "Visitor", id: user.id }),
          fields: {
            connections: appendItem(
              createPendingData.createPendingConnection.connection
            ),
          },
        });
      },
      onCompleted: onResubmitted,
    });

  const [
    createPendingProfessionalConnection,
    { loading: resubmittingProfessional },
  ] = useMutation(CreatePendingProfessionalConnectionDocument, {
    onError: onResubmitError,
    update: (cache, { data: createPendingData }) => {
      if (!createPendingData) return;
      cache.modify({
        id: cache.identify({ __typename: "Visitor", id: user.id }),
        fields: {
          connections: appendItem(
            createPendingData.createPendingProfessionalConnection.connection
          ),
        },
      });
    },
    onCompleted: onResubmitted,
  });

  const resubmitting = resubmittingPersonal || resubmittingProfessional;

  if (error) throw error;
  if (!data) return <ContentSkeleton />;

  const { connection } = data;
  if (!submitted && connection.status !== ConnectionStatus.Rejected)
    return <Navigate to={`/contacts/${connectionId}`} replace />;

  const { visitationFormLink } = connection.inmate.facility;
  // organization-backed connections get their relationship from the
  // organization type, so it can't be chosen
  const { organizationMembership } = connection;
  const canEditRelationship = !organizationMembership;
  const editedRelationship = canEditRelationship
    ? searchParams.get("relationship")
    : null;
  const relationship = editedRelationship ?? connection.relationship;

  const hasApprovedFacility = user.systemRelationships.some(
    (sr) => sr.status === SystemUserStatus.Approved
  );

  const resubmitPath = `/contacts/${connectionId}/resubmit${
    editedRelationship
      ? `?${new URLSearchParams({ relationship: editedRelationship })}`
      : ""
  }`;
  const editIdentification = () =>
    navigate("/settings/identification", {
      state: {
        action: user.identity ? "edit" : "create",
        returnTo: resubmitPath,
      },
    });

  return (
    <Screen
      showBack
      backFallback={`/contacts/${connectionId}`}
      title={connection.inmate.fullName}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h2" sx={{ mb: 1 }}>
            {t("Review and Resubmit")}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {t(
              "Please review the facility's message and follow any guidance before resubmitting."
            )}
          </Typography>
        </Box>
        {connection.statusDetails && (
          <Card variant="outlined" sx={{ p: 2, backgroundColor: orange[50] }}>
            <Quotation variant="body2">{connection.statusDetails}</Quotation>
          </Card>
        )}
        <EditableCard
          onEdit={
            canEditRelationship
              ? () => setEditRelationshipShown(true)
              : undefined
          }
        >
          <DetailsStackItem
            label={t("Contact name")}
            value={connection.inmate.fullName}
          />
          <DetailsStackItem
            label={t("Relationship")}
            value={relationshipToHumanReadable(relationship)}
          />
        </EditableCard>
        <EditableCard
          onEdit={() =>
            hasApprovedFacility
              ? setConfirmEditIdShown(true)
              : editIdentification()
          }
        >
          {user.identity && (
            <DetailsStackItem
              label={t("ID")}
              value={getDocumentTypeLabel(user.identity.documentType)}
            />
          )}
          <DetailsStackItem label={t("Your name")} value={user.fullName} />
          {user.dateOfBirth && (
            <DetailsStackItem
              label={t("Date of birth")}
              value={monthDateYearFormat.format(
                getRawDateTzUnaware(user.dateOfBirth)
              )}
            />
          )}
        </EditableCard>
        {visitationFormLink && (
          <Card variant="outlined" sx={{ p: 2 }}>
            <DetailsStackItem label={t("Visitor form")}>
              <ExternalLink href={visitationFormLink} target="_blank">
                {getHostname(visitationFormLink) ?? visitationFormLink}
              </ExternalLink>
            </DetailsStackItem>
          </Card>
        )}
        <Box>
          <Button
            variant="contained"
            disabled={resubmitting}
            onClick={() => {
              setSubmitted(true);
              if (organizationMembership)
                createPendingProfessionalConnection({
                  variables: {
                    input: {
                      organizationMembershipId: organizationMembership.id,
                      inmateId: connection.inmate.id,
                    },
                  },
                });
              else
                createPendingConnection({
                  variables: {
                    input: {
                      inmateId: connection.inmate.id,
                      relationship,
                    },
                  },
                });
            }}
          >
            {t("Resubmit")}
          </Button>
        </Box>
      </Stack>
      {canEditRelationship && editRelationshipShown && (
        <EditConnectionRelationshipDialog
          relationship={relationship}
          connectionId={connectionId}
          onSave={(newRelationship) =>
            setSearchParams(
              { relationship: newRelationship },
              { replace: true }
            )
          }
          onClose={() => setEditRelationshipShown(false)}
        />
      )}
      {confirmEditIdShown && (
        <ConfirmDialog
          title={t("Are you sure you want to edit your Identification?")}
          description={`${t(
            "Editing your personal information will cancel all upcoming events you have at any facility."
          )} ${
            user.identity
              ? t("Each facility will have to review your ID again.")
              : t(
                  "Any approved or pending contact requests will be archived and must be re-submitted."
                )
          }`}
          confirmText={t("Yes, continue")}
          onConfirm={() => {
            setConfirmEditIdShown(false);
            editIdentification();
          }}
          onCancel={() => setConfirmEditIdShown(false)}
        />
      )}
    </Screen>
  );
}
